import { COMPANY_TYPE_LABELS, type Company } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  Drawer,
  EmptyState,
  PageHeader,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyForm } from "../../../features/company-form.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { useDocumentTitle } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";
import { useMe, usePermissions } from "../../../lib/session.js";

export const Route = createFileRoute("/_app/ayarlar/sirketler")({ component: CompaniesPage });

function CompaniesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const features = useMe().data?.activeTenant?.features ?? [];
  useDocumentTitle(t("menu.companies"));

  const [panel, setPanel] = useState<{ mode: "create" } | { mode: "edit"; company: Company } | null>(null);
  const companies = useQuery({
    queryKey: queryKeys.companies,
    queryFn: async () => (await api.GET("/companies")).data!.items,
  });
  const canManage = permissions.has("org.company.manage");
  // Birden fazla şirket bir plan özelliğidir; ilk şirket her planda eklenebilir.
  const canAdd = canManage && ((companies.data?.length ?? 0) === 0 || features.includes("MULTI_COMPANY"));

  const setActive = useMutation({
    mutationFn: async (input: { company: Company; isActive: boolean }) =>
      api.PATCH("/companies/{id}", {
        params: { path: { id: input.company.id } },
        body: { isActive: input.isActive, version: input.company.version },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.companies }),
        queryClient.invalidateQueries({ queryKey: queryKeys.me }),
      ]);
      setPanel(null);
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const columns = useMemo<DataTableColumn<Company>[]>(
    () => [
      {
        id: "legalName",
        header: t("companies.fields.legalName"),
        hideable: false,
        cell: (row) => <span className="font-medium">{row.legalName}</span>,
        exportValue: (row) => row.legalName,
      },
      {
        id: "shortName",
        header: t("companies.fields.shortName"),
        cell: (row) => row.shortName ?? "—",
        exportValue: (row) => row.shortName,
        defaultVisible: false,
      },
      {
        id: "companyType",
        header: t("companies.fields.companyType"),
        cell: (row) => COMPANY_TYPE_LABELS[row.companyType],
        exportValue: (row) => COMPANY_TYPE_LABELS[row.companyType],
      },
      {
        id: "taxId",
        header: t("companies.fields.taxId"),
        cell: (row) => <span className="tabular font-mono">{row.taxId}</span>,
        exportValue: (row) => row.taxId,
      },
      {
        id: "taxOffice",
        header: t("companies.fields.taxOffice"),
        cell: (row) => row.taxOffice ?? "—",
        exportValue: (row) => row.taxOffice,
      },
      {
        id: "city",
        header: t("common.city"),
        cell: (row) => [row.district, row.city].filter(Boolean).join(" / ") || "—",
        exportValue: (row) => row.city,
      },
      {
        id: "eInvoice",
        header: t("companies.eInvoice"),
        cell: (row) =>
          row.isEInvoiceRegistered ? (
            <StatusBadge tone="info">{t("companies.eInvoiceRegistered")}</StatusBadge>
          ) : (
            "—"
          ),
        exportValue: (row) => (row.isEInvoiceRegistered ? t("common.yes") : t("common.no")),
      },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
        exportValue: (row) => (row.isActive ? t("common.active") : t("common.inactive")),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t("menu.companies")}
        description={t("companies.description")}
        breadcrumb={[{ label: t("menu.groups.settings") }, { label: t("menu.companies") }]}
        actions={
          canAdd ? (
            <Button onClick={() => setPanel({ mode: "create" })}>
              <Plus aria-hidden="true" />
              {t("companies.add")}
            </Button>
          ) : null
        }
      />
      {canManage && !canAdd ? (
        <Alert tone="info" className="mb-4">
          {t("companies.multiCompanyNotInPlan")}
        </Alert>
      ) : null}
      <DataTable
        caption={t("menu.companies")}
        columns={columns}
        rows={companies.data ?? []}
        rowKey={(row) => row.id}
        loading={companies.isPending}
        error={companies.error ? <Alert tone="danger">{errorMessage(companies.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            icon={<Building2 />}
            title={t("companies.empty.title")}
            description={t("companies.empty.description")}
          />
        }
        onExport={(visible) =>
          exportRowsToExcel({
            fileName: "sirketler",
            sheetName: t("menu.companies"),
            columns: visible,
            rows: companies.data ?? [],
          })
        }
        {...(canManage ? { onRowClick: (company: Company) => setPanel({ mode: "edit", company }) } : {})}
      />

      <Drawer
        open={panel !== null}
        onOpenChange={(open) => !open && setPanel(null)}
        title={panel?.mode === "edit" ? panel.company.legalName : t("companies.add")}
        {...(panel?.mode === "edit" ? { description: t("companies.editDescription") } : {})}
      >
        {panel ? (
          <div className="grid gap-6">
            <CompanyForm
              key={panel.mode === "edit" ? `${panel.company.id}-${panel.company.version}` : "new"}
              {...(panel.mode === "edit" ? { company: panel.company } : {})}
              onCancel={() => setPanel(null)}
              onSaved={() => {
                setPanel(null);
                toast({ tone: "success", title: t("common.saved") });
              }}
            />
            {panel.mode === "edit" ? (
              <section className="grid gap-2 border-t pt-4">
                <h2 className="text-sm font-semibold">{t("common.status")}</h2>
                <p className="text-sm text-muted-foreground">
                  {panel.company.isActive ? t("companies.deactivateHint") : t("companies.activateHint")}
                </p>
                <Button
                  variant="outline"
                  className="justify-self-start"
                  loading={setActive.isPending}
                  onClick={() =>
                    setActive.mutate({ company: panel.company, isActive: !panel.company.isActive })
                  }
                >
                  {panel.company.isActive ? t("common.deactivate") : t("common.activate")}
                </Button>
              </section>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
